import { RouteClient } from '@uniformdev/canvas';
import type { CompositionParameterValue } from '@/core/utils/uniform/get-composition-parameter-value';
import {
  APP_PDP_HEADER_SUMMARY_LINK_CTA,
  APP_PDP_HEADER_SUMMARY_NAV,
} from '@/core/constants/app-pdp-header-summary';
import { isBrowsingWithoutOrderFlowLocale } from '@/core/constants/browse-only-resolution';
import { UNIFORM_API_KEY, UNIFORM_PROJECT_ID } from '@/core/constants/env';
import { getBrowseOnlyUniformRootSlug } from '@/core/constants/non-ecommerce-markets';
import fetchEvaOrganizations from '@/core/services/eva/fetch-eva-organizations';
import getCompositionBySlug from '@/core/services/uniform/get-composition-by-slug';
import pathnameWithoutLocale from '@/core/utils/pathname-without-locale';
import { getCompositionParameterValue } from '@/core/utils/uniform/get-composition-parameter-value';
import isUniformConfigured from '@/core/utils/uniform/is-uniform-configured';
import pathnameToSlug from '@/core/utils/uniform/pathname-to-slug';
import { resolveUniformApiLanguage } from '@/core/utils/uniform/resolve-uniform-api-language';
import {
  type ContentLinkSlot,
  resolveContentLink,
} from '@/ui/canvas/adapters/resolveContentLink';

export type HeaderSummaryNavItem = {
  sectionId: string;
  title: string;
};

export type HeaderSummaryCtaType = 'product' | 'link';

export type HeaderSummaryCta = {
  label: string;
  href: string;
  isExternal?: boolean;
  ariaLabel?: string;
};

export type HeaderSummaryConfig = {
  items: HeaderSummaryNavItem[];
  ctaType: HeaderSummaryCtaType;
  cta?: HeaderSummaryCta;
};

type HeaderSummaryNavItemSlot = {
  parameters?: {
    sectionId?: CompositionParameterValue;
    label?: CompositionParameterValue;
  };
};

export type PageComposition = {
  parameters?: Record<
    string,
    CompositionParameterValue | string | undefined
  >;
  slots?: {
    content?: Array<{ type?: string }>;
    headerSummary?: HeaderSummaryNavItemSlot[];
    headerSummaryCta?: ContentLinkSlot[];
  };
};

export const EMPTY_HEADER_SUMMARY_CONFIG: HeaderSummaryConfig = {
  items: [],
  ctaType: 'product',
};

type UniformLabels = Record<string, string>;

function isAppPdpComposition(composition: PageComposition): boolean {
  return (
    composition.slots?.content?.some(item => item.type === 'appHero') ?? false
  );
}

function buildAppPdpNavItems(labels: UniformLabels): HeaderSummaryNavItem[] {
  const items: HeaderSummaryNavItem[] = [];

  for (const { sectionId, labelKey } of APP_PDP_HEADER_SUMMARY_NAV) {
    const title = labels[labelKey];
    if (title) {
      items.push({ sectionId, title });
    }
  }

  return items;
}

function buildAppPdpLinkCta(labels: UniformLabels): HeaderSummaryCta | undefined {
  const label = labels[APP_PDP_HEADER_SUMMARY_LINK_CTA.labelKey];
  if (!label) {
    return undefined;
  }

  return {
    label,
    href: APP_PDP_HEADER_SUMMARY_LINK_CTA.href,
    isExternal: false,
  };
}

function parseHeaderSummaryCtaType(
  composition: PageComposition,
  locale?: string,
  labels?: UniformLabels,
): HeaderSummaryCtaType {
  const param = composition.parameters?.headerSummaryCtaType;
  if (typeof param === 'string') {
    return param === 'link' ? 'link' : 'product';
  }

  const value = getCompositionParameterValue(param, locale, labels);
  if (value === 'link') {
    return 'link';
  }

  if (composition.slots?.headerSummaryCta?.length) {
    return 'link';
  }

  return 'product';
}

function parseHeaderSummaryNavItems(
  items: HeaderSummaryNavItemSlot[] | undefined,
  locale?: string,
  labels?: UniformLabels,
): HeaderSummaryNavItem[] {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  return items
    .map(item => {
      const sectionId = getCompositionParameterValue(
        item.parameters?.sectionId,
        locale,
        labels,
      );
      const title = getCompositionParameterValue(
        item.parameters?.label,
        locale,
        labels,
      );
      if (!sectionId || title == null) return null;
      return { sectionId, title };
    })
    .filter((item): item is HeaderSummaryNavItem => item != null);
}

function extractHeaderSummaryLinkCta(
  composition: PageComposition,
  locale?: string,
  labels?: UniformLabels,
): HeaderSummaryCta | undefined {
  const linkSlot = composition.slots?.headerSummaryCta?.[0];
  if (!linkSlot || linkSlot.type !== 'contentLink') {
    return undefined;
  }

  const resolved = resolveContentLink(linkSlot);
  if (!resolved?.href) {
    return undefined;
  }

  const label =
    getCompositionParameterValue(
      linkSlot.parameters?.label,
      locale,
      labels,
    ) ?? resolved.label;

  if (!label) {
    return undefined;
  }

  const ariaLabel =
    getCompositionParameterValue(
      linkSlot.parameters?.ariaLabel,
      locale,
      labels,
    ) ?? resolved.ariaLabel;

  return {
    label,
    href: resolved.href,
    isExternal: resolved.isExternal,
    ariaLabel,
  };
}

function supplementAppPdpHeaderSummary(
  config: HeaderSummaryConfig,
  labels: UniformLabels,
): HeaderSummaryConfig {
  const items =
    config.items.length > 0 ? config.items : buildAppPdpNavItems(labels);
  const ctaType = config.ctaType === 'product' ? 'link' : config.ctaType;
  const cta =
    ctaType === 'link'
      ? (config.cta ?? buildAppPdpLinkCta(labels))
      : config.cta;

  return { items, ctaType, cta };
}

export function parseHeaderSummaryFromComposition(
  composition: PageComposition | null | undefined,
  locale?: string,
  labels?: UniformLabels,
): HeaderSummaryConfig {
  if (!composition) {
    return EMPTY_HEADER_SUMMARY_CONFIG;
  }

  const ctaType = parseHeaderSummaryCtaType(composition, locale, labels);

  const config: HeaderSummaryConfig = {
    items: parseHeaderSummaryNavItems(
      composition.slots?.headerSummary,
      locale,
      labels,
    ),
    ctaType,
  };

  if (ctaType === 'link') {
    const cta = extractHeaderSummaryLinkCta(composition, locale, labels);
    if (cta) {
      config.cta = cta;
    }
  }

  if (isAppPdpComposition(composition) && labels) {
    return supplementAppPdpHeaderSummary(config, labels);
  }

  return config;
}

async function isBrowseOnlyRootPath(
  pathname: string,
  locale: string,
): Promise<boolean> {
  if (pathnameToSlug(pathname) !== '') {
    return false;
  }

  const organizations = await fetchEvaOrganizations().catch(() => []);
  const evaCountryIds = organizations.map(org => org.countryId);

  return isBrowsingWithoutOrderFlowLocale(locale, evaCountryIds);
}

async function fetchPageCompositionForHeaderSummary(
  pathname: string,
  locale: string,
): Promise<PageComposition | null> {
  if (await isBrowseOnlyRootPath(pathname, locale)) {
    return (await getCompositionBySlug(
      getBrowseOnlyUniformRootSlug(locale),
      locale,
      'page header summary config',
    )) as PageComposition | null;
  }

  if (!isUniformConfigured()) {
    return null;
  }

  try {
    const client = new RouteClient({
      apiKey: UNIFORM_API_KEY,
      projectId: UNIFORM_PROJECT_ID,
    });

    const route = await client.getRoute({
      path: pathnameWithoutLocale(pathname),
      locale: resolveUniformApiLanguage(locale),
    });

    if (route.type !== 'composition') {
      return null;
    }

    return (route.compositionApiResponse?.composition ??
      null) as PageComposition | null;
  } catch (error) {
    console.warn(
      'Failed to fetch page header summary config from Uniform route:',
      error instanceof Error ? error.message : error,
    );
    return null;
  }
}

export async function fetchPageHeaderSummaryConfig(
  pathname: string,
  locale: string,
  labels?: UniformLabels,
): Promise<HeaderSummaryConfig> {
  const composition = await fetchPageCompositionForHeaderSummary(
    pathname,
    locale,
  );

  if (!composition) return EMPTY_HEADER_SUMMARY_CONFIG;

  return parseHeaderSummaryFromComposition(composition, locale, labels);
}
